import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Phone, Star, Award, CheckCircle2 } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Counter from "@/components/Counter";
import { IMAGES, SERVICES, STATS, TESTIMONIALS, PROCESS, INSURERS, CHAPTERS, CONTACT } from "@/lib/data";
import GoogleReviewsBadge from "@/components/GoogleReviewsBadge";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const HERO_SLIDES = [
    { src: IMAGES.heroHero, alt: "Klassiker in der Halle · Mercedes 190 SL, Jaguar XK 150, Cadillac, Ferrari F40" },
    { src: IMAGES.heroHero2, alt: "Björn Linkowitz im Classic-Data Showroom" },
  ];
  const [slide, setSlide] = useState(0);

  useEffect(() => { document.title = "Classic Data Berlin — Oldtimer- & Youngtimer-Gutachten"; }, []);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page" className="bg-[color:var(--cream)]">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] w-full overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale, opacity: heroOpacity }} className="absolute inset-0">
          <AnimatePresence>
            <motion.img
              key={slide}
              src={HERO_SLIDES[slide].src}
              alt={HERO_SLIDES[slide].alt}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1.6, ease: [0.22, 1, 0.36, 1] }, scale: { duration: 7, ease: "linear" } }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-[color:var(--cream)]" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-[38vh] md:pt-[36vh] pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} className="eyebrow !text-[color:var(--cream)]/90">
            <span className="inline-block mr-3">— Est. 2002 · Berlin</span>
          </motion.div>

          <LineReveal
            className="display-xl text-[color:var(--cream)] mt-6 text-[14vw] md:text-[9.5vw] lg:text-[8.2vw] leading-[0.9]"
            lines={["Jeder Klassiker", "hat seine Geschichte.", "Wir bestimmen"]}
            delay={0.3}
          />
          <LineReveal
            className="display-xl text-[color:var(--cream)] italic text-[14vw] md:text-[9.5vw] lg:text-[8.2vw] leading-[0.9]"
            lines={["seinen wahren Wert."]}
            delay={0.65}
          />

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.4 }} className="mt-10 max-w-xl">
            <p className="text-[color:var(--cream)] text-lg leading-relaxed drop-shadow-md">
              Unabhängige Wertgutachten für Oldtimer, Sammlerfahrzeuge und Modern Classics — mit Präzision, Erfahrung und Leidenschaft. Seit 2002 in Berlin und darüber hinaus.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/kontakt" data-testid="hero-cta-primary" className="btn-primary">Gutachten anfragen <ArrowUpRight className="h-4 w-4" /></Link>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-cta-secondary" className="btn-ghost !bg-transparent !text-[color:var(--cream)] !border-white/50 hover:!bg-[color:var(--cream)] hover:!text-[color:var(--ink)]">
                <Phone className="h-4 w-4" /> Experte anrufen
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }} className="absolute bottom-8 left-6 lg:left-10 z-10 text-[color:var(--cream)] text-[10px] tracking-[0.24em] uppercase">
          Scrollen — Kapitel 01
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }} className="absolute bottom-8 right-6 lg:right-10 z-10 text-[color:var(--cream)] text-[10px] tracking-[0.24em] uppercase text-right">
          KFZ-Sachverständigenbüro<br />Björn Linkowitz
        </motion.div>

        {/* Slider dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={i}
              aria-label={`Bild ${i + 1} anzeigen`}
              data-testid={`hero-dot-${i}`}
              onClick={() => setSlide(i)}
              className={`relative h-[2px] transition-all duration-500 ${slide === i ? "w-14 bg-[color:var(--cream)]" : "w-8 bg-[color:var(--cream)]/40 hover:bg-[color:var(--cream)]/70"}`}
            />
          ))}
        </div>
      </section>

      {/* INTRO / CHAPTER 01 */}
      <section className="py-32 lg:py-40 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <div className="eyebrow mb-6">— Kapitel 01 · Willkommen</div>
            <div className="font-serif text-6xl lg:text-7xl leading-none">01</div>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.15}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Bewertungen für <em className="italic">Oldtimer</em> und <em className="italic">Youngtimer</em> — belastbar, unabhängig, persönlich.
            </h2>
            <div className="mt-8 space-y-5 text-[17px] leading-[1.75] text-[color:var(--ink-2)] max-w-2xl">
              <p>Oldtimer und Youngtimer erfreuen sich immer größerer Beliebtheit — was sich auch an ihren Marktpreisen ablesen lässt. Die Liebhaber und Besitzer klassischer Fahrzeuge konnten sich in den letzten Jahren über beachtliche Wertzuwächse ihrer Automobile freuen.</p>
              <p>Umso wichtiger ist es, dass diese Fahrzeuge <em className="font-serif italic text-[color:var(--ink)]">angemessen versichert</em> sind und der stolze Besitzer im Schadenfall nicht nur den Schrottpreis pro Kilo erstattet bekommt.</p>
              <p>Seit 2002 ist Björn Linkowitz Vertragspartner der Classic Data Marktbeobachtung GmbH für klassische Fahrzeuge und vom BVSK offiziell anerkannt als Sachverständiger für historische Fahrzeuge.</p>
            </div>
            <div className="mt-8">
              <Link to="/ueber-uns" data-testid="intro-more-link" className="link-underline text-sm tracking-[0.14em] uppercase font-medium">Mehr über uns</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* SERVICES */}
      <section className="py-32 lg:py-40 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <Reveal className="lg:col-span-6">
              <div className="eyebrow mb-6">— Kapitel 02 · Leistungen</div>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                Drei Gutachten. <span className="italic">Ein Anspruch:</span> Ihr Fahrzeug fair zu bewerten.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.15}>
              <p className="text-[color:var(--ink-2)] leading-relaxed text-[17px]">
                Ob Kurzbewertung für die Versicherung, ausführliches Wertgutachten beim Kauf oder Verkauf oder Schadengutachten nach einem Unfall — jedes Gutachten trägt dieselbe Handschrift: neutral, sorgfältig und dokumentiert.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.12}>
                <Link to={`/leistungen/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block">
                  <div className="relative overflow-hidden aspect-[3/4] bg-black">
                    <img src={IMAGES[s.image]} alt={s.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-[900ms] ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute top-5 left-5 text-[color:var(--cream)] font-serif text-sm tracking-[0.24em]">{s.number}</div>
                    <div className="absolute top-5 right-5 text-[color:var(--cream)] opacity-0 group-hover:opacity-100 transition-opacity"><ArrowUpRight className="h-5 w-5" /></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-[color:var(--cream)]">
                      <div className="text-[10px] tracking-[0.24em] uppercase mb-2 opacity-80">{s.tagline}</div>
                      <div className="font-serif text-2xl lg:text-3xl leading-tight">{s.title}</div>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-[color:var(--ink-2)] leading-relaxed">{s.short}</p>
                  <div className="mt-4 text-xs tracking-[0.14em] uppercase font-medium link-underline">Mehr erfahren</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL SPREAD — expert image + burgundy panel */}
      <section className="py-24 lg:py-32 px-6 lg:px-10 bg-[color:var(--cream-2)]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <Reveal className="lg:col-span-7 relative">
            <div className="relative overflow-hidden aspect-[4/5] lg:aspect-[5/6] bg-black">
              <img src={IMAGES.expertCadillac} alt="Björn Linkowitz bei der Inaugenscheinnahme" className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 text-xs text-[color:var(--ink-2)] flex justify-between">
              <span>Fig. 01 — Inaugenscheinnahme, Cadillac Series 62</span>
              <span>Hoppegarten, Berlin</span>
            </div>
          </Reveal>
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal>
              <div className="eyebrow mb-6">— Kapitel 03 · Manifest</div>
              <div className="space-y-10">
                {CHAPTERS.map((c) => (
                  <div key={c.n} className="grid grid-cols-12 gap-4">
                    <div className="col-span-2 font-serif text-3xl text-[color:var(--burgundy)]">{c.n}</div>
                    <div className="col-span-10">
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ink-2)] mb-2">{c.kicker}</div>
                      <h3 className="font-serif text-2xl leading-[1.15] mb-2">{c.title}</h3>
                      <p className="text-[color:var(--ink-2)] leading-relaxed text-[15px]">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-28 lg:py-36 px-6 lg:px-10 bg-[color:var(--ink)] text-[color:var(--cream)]">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="eyebrow !text-[color:var(--cream)]/60 mb-6">— Kapitel 04 · In Zahlen</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Zwei Jahrzehnte klassischer Automobile — <span className="italic">in Zahlen.</span></h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1}>
                <div className="border-t border-white/25 pt-6">
                  <div className="font-serif text-6xl md:text-7xl lg:text-8xl leading-none tracking-tight"><Counter to={s.value} suffix={s.suffix} /></div>
                  <div className="mt-4 text-sm font-medium tracking-wide">{s.label}</div>
                  <div className="text-xs text-white/60 mt-1 max-w-[220px]">{s.note}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-32 lg:py-40 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <Reveal className="lg:col-span-7">
              <div className="eyebrow mb-6">— Kapitel 05 · Ablauf</div>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                Vier Schritte. <span className="italic">Ein Weg</span> zum belastbaren Gutachten.
              </h2>
            </Reveal>
          </div>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-black/15 hidden md:block" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-4">
              {PROCESS.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.1}>
                  <div className="pl-0 md:pl-14 relative">
                    <div className="hidden md:block absolute left-[19px] top-2 w-3 h-3 bg-[color:var(--burgundy)] rounded-full" />
                    <div className="font-serif text-6xl md:text-7xl text-[color:var(--burgundy)]/25">{p.n}</div>
                    <h3 className="mt-2 font-serif text-2xl">{p.title}</h3>
                    <p className="mt-3 text-sm text-[color:var(--ink-2)] leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-20 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between border-t border-black/15 pt-10">
            <div>
              <div className="font-serif text-2xl md:text-3xl">Sie rufen an, <span className="italic">wir kommen vor Ort.</span></div>
              <div className="mt-2 text-[color:var(--ink-2)] text-sm">Bequem online oder telefonisch — unter <a href={`tel:${CONTACT.phoneRaw}`} className="link-underline text-[color:var(--ink)]">{CONTACT.phone}</a></div>
            </div>
            <Link to="/kontakt" className="btn-primary" data-testid="process-cta">Termin vereinbaren <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32 lg:py-40 px-6 lg:px-10 bg-[color:var(--cream-2)]">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="eyebrow mb-6">— Kapitel 06 · Referenzen</div>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
              Was unsere <span className="italic">Kundinnen und Kunden</span> sagen.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <article className="bg-[color:var(--cream)] border border-black/10 p-8 h-full flex flex-col">
                  <div className="flex gap-1 text-[color:var(--burgundy)] mb-6">
                    {[...Array(t.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                  </div>
                  <blockquote className="font-serif text-xl md:text-[22px] leading-[1.35] text-[color:var(--ink)] flex-1">
                    {`„${t.quote}"`}
                  </blockquote>
                  <div className="mt-8 pt-6 border-t border-black/10 flex items-center gap-4">
                    <div className="w-14 h-14 overflow-hidden bg-black shrink-0">
                      <img src={IMAGES[t.image]} alt={t.car} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{t.name}</div>
                      <div className="text-xs text-[color:var(--ink-2)] italic">{t.car}</div>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 flex flex-col sm:flex-row justify-center items-center gap-6">
            <GoogleReviewsBadge />
            <Link to="/referenzen" className="btn-ghost" data-testid="testimonials-more">Alle Referenzen ansehen <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      {/* INSURERS */}
      <section className="py-24 lg:py-28 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
              <div className="lg:col-span-6">
                <div className="eyebrow mb-6">— Kapitel 07 · Anerkennung</div>
                <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">Anerkannt von den führenden <em className="italic">Klassiker­versicherungen.</em></h2>
              </div>
              <p className="lg:col-span-5 lg:col-start-8 text-[color:var(--ink-2)] leading-relaxed">
                Unsere Gutachten werden von allen relevanten Versicherern akzeptiert — Ihre Klassiker sind damit versicherungstechnisch auf der sicheren Seite.
              </p>
            </div>
          </Reveal>
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-black/10">
              {INSURERS.map((n) => (
                <div key={n} className="border-r border-b border-black/10 px-6 py-8 flex items-center justify-center text-center hover:bg-[color:var(--burgundy)] hover:text-[color:var(--cream)] transition-colors duration-300">
                  <span className="font-serif text-lg">{n}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.office} alt="Büro Hoppegarten" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#7C1C2E]/90 mix-blend-multiply" />
          <div className="absolute inset-0 bg-[#7C1C2E]/40" />
        </div>
        <div className="relative py-28 lg:py-36 px-6 lg:px-10 text-[color:var(--cream)]">
          <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[10px] tracking-[0.24em] uppercase mb-6 opacity-80">— Beratung</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                Fragen zu Ihrem Klassiker? <br /><em className="italic">Unsere Experten sind gerne für Sie da.</em>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex flex-col gap-3">
              <Link to="/kontakt" className="btn-primary !bg-[color:var(--cream)] !text-[color:var(--burgundy)] !border-[color:var(--cream)] hover:!bg-[color:var(--ink)] hover:!text-[color:var(--cream)] hover:!border-[color:var(--ink)]" data-testid="cta-band-primary">
                Rückruf vereinbaren <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-ghost !text-[color:var(--cream)] !border-white/50 hover:!bg-[color:var(--cream)] hover:!text-[color:var(--burgundy)]" data-testid="cta-band-call">
                <Phone className="h-4 w-4" /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
